import clsx from 'clsx';
import React, { Ref, useEffect, useRef } from 'react';
import {
  DataFunc,
  Mention,
  MentionsInput,
  MentionsInputProps,
} from 'react-mentions';
import { mergeRefs } from 'react-merge-refs';
import { OnTagsChangeEvent, convertTags, isAnyNewTags } from './tags-helpers';
import styles from './TagsEditor.module.scss';

import searchStyles from '../inputs/search/SearchInput.module.scss';
import inputStyles from '../inputs/Input.module.scss';
import { useTagsForMentions } from './useTags';
import CrossIcon from '@lmt/unicorn-shared/app/icons/search-cross.svg?react';
import SearchIcon from '@lmt/unicorn-shared/app/icons/search.svg?react';
import { useCrossIconClick } from '../inputs/dropdown/SearchInput';
import ReactDOM from 'react-dom';
import { InputTemplateBadge } from '../../../editor/input-templates/badge/InputTemplateBadge';
import { useEventCallback } from '@mui/material';

type Props = {
  value: string;
  onChange: (e: OnTagsChangeEvent, value: string) => void;
  isMultiline: boolean;
  createNonExistingTags?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  isHeightStable?: boolean;
  maxAutoHeight?: number;
  maxLength?: number;
  className?: string;
  containerRef?: Ref<HTMLDivElement>;
  endAdornment?: React.ReactNode;
  isSearch?: boolean;
  isTemplateVisible?: boolean;
  onTemplateClick?: () => void; // search or template

  /*
   * This prop might be set if input is part of Autocomplete (DropDown/Combobox/etc).
   * If we don't do this, `selectedValue` ends up as DOM attribute which isn't what we want.
   * It might be better implemented by wrapping inputs that are used in Autocomplete, but it's less of a hassle like it is now.
   */
  selectedValue?: unknown;
} & Omit<MentionsInputProps, 'children'>;

export const TagsEditor = React.forwardRef<HTMLTextAreaElement, Props>(
  function TagsEditor(props, ref) {
    const {
      value,
      onChange,
      helperText,
      error,
      isMultiline,
      placeholder,
      maxLength,
      createNonExistingTags: createNonExistingTagsProp = true,
      className,
      containerRef,
      isSearch,
      endAdornment,
      isHeightStable,
      maxAutoHeight,
      onTemplateClick,
      isTemplateVisible,
      selectedValue,
      ...rest
    } = props;
    const valueRef = useRef(value);
    valueRef.current = value;

    const onChangeAndResize: typeof onChange = (event, value) => {
      onChange(event, value);
      resizeCommentTextBox();
    };
    const existingTags = useTagsForMentions();
    const suggestionsPortalHostRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isAutosizeEnabled = isMultiline;
    const resizeCommentTextBox = useEventCallback(() => {
      if (!inputRef.current) return;

      if (isAutosizeEnabled) {
        inputRef.current.style.height = '';

        if (isHeightStable) {
          inputRef.current.style.height = maxAutoHeight + 'px';
        } else {
          inputRef.current.style.height =
            (props.maxAutoHeight
              ? Math.min(inputRef.current.scrollHeight + 3, props.maxAutoHeight)
              : inputRef.current.scrollHeight + 3) + 'px';
        }

        // inputRef.current.style.maxHeight = 'unset';
        // inputRef.current.style.minHeight = 'unset';
      }
    });
    useEffect(() => {
      setTimeout(resizeCommentTextBox, 1);
    }, []);

    const onCommentChange = useEventCallback(
      async (event: OnTagsChangeEvent) => {
        // MaxLength is not accounted for when pasting text into MentionsInput
        // https://github.com/signavio/react-mentions/issues/616
        // https://github.com/signavio/react-mentions/issues/479
        if (maxLength)
          event.target.value = event.target.value.substring(0, maxLength);

        const comment = event.target.value;
        if (
          createNonExistingTagsProp &&
          // we only need to create tags if last pressed key was a space, or when focus was lost.
          // otherwise it's impossible to create a tag in the middle of existing text (tag is created with single letter)
          // e.g. you had text 'asd  zxc' and then want to change it into 'asd #qwe zxc'
          (lastKeyDown.current === ' ' ||
            lastKeyDown.current === '\r' ||
            lastKeyDown.current === '\n')
        ) {
          await convertTags(
            comment,
            (changeFunc) => {
              const newComment = changeFunc(valueRef.current);
              onChangeAndResize(event, newComment);
            },
            existingTags,
          );
        } else {
          onChangeAndResize(event, comment);
        }
      },
    );
    const crossIconClickHandler = useCrossIconClick(
      inputRef as any,
      onCommentChange,
    );
    const onSearchIconClicked = useEventCallback(() => {
      const keyboardEvent = new window.KeyboardEvent('keydown', {
        code: '\n',
        key: 'Enter',
        bubbles: true,
      });

      inputRef.current?.dispatchEvent(keyboardEvent);
    });

    const dataFilterFunction: DataFunc = useEventCallback((q) => {
      // only show 9 tags max so they are shown without scroll
      // (otherwise see #46657: Quick search list is not scrolled using arrows)
      return existingTags
        .filter((x) => x.display.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 9);
    });
    const lastKeyDown = useRef('');
    const onBlur = useEventCallback(async () => {
      if (!isAnyNewTags(valueRef.current)) return;
      lastKeyDown.current = ' ';
      await onCommentChange({
        target: {
          value: valueRef.current,
        },
      });
    });
    const onKeyDown = useEventCallback(
      (
        event:
          | React.KeyboardEvent<HTMLTextAreaElement>
          | React.KeyboardEvent<HTMLInputElement>,
      ) => {
        lastKeyDown.current = event.key;
      },
    );
    return (
      <>
        {ReactDOM.createPortal(
          <div
            className={styles.suggestionsPortal}
            ref={suggestionsPortalHostRef}
          />,
          document.body,
        )}
        <div
          data-comment={isMultiline}
          data-error={error}
          className={clsx(styles.mentionsParent, className)}
          ref={containerRef}
        >
          <MentionsInput
            {...rest}
            inputRef={mergeRefs([inputRef, ref])}
            onKeyDownCapture={onKeyDown}
            value={value || ''}
            onBlur={onBlur}
            onChange={onCommentChange}
            className={clsx(
              styles.mentionsInput,
              'mentions',
              isSearch && styles.inputWithSearchIcon,
              (props.maxAutoHeight || isMultiline) && styles.autoheight,
            )}
            maxLength={maxLength}
            singleLine={!isMultiline}
            allowSpaceInQuery={false}
            suggestionsPortalHost={
              suggestionsPortalHostRef.current ?? undefined
            }
            placeholder={placeholder}
          >
            <Mention
              markup="[__display__]{__id__}"
              trigger="#"
              data={dataFilterFunction}
              className={styles.mentions__mention}
              appendSpaceOnAdd={true}
            />
          </MentionsInput>
          {isSearch && (
            <div className={inputStyles.endAdornment}>
              <CrossIcon
                className={
                  value.length === 0 ? searchStyles.hidden : styles.crossIcon
                }
                onClick={crossIconClickHandler}
                data-test-id="global-cross-icon"
              />
              <SearchIcon
                className={styles.searchIcon}
                onClick={onSearchIconClicked}
                data-test-id="globalSearchIcon"
              />
            </div>
          )}
          {!isSearch && !!onTemplateClick && (
            <div className={styles.endAdornment} data-hover={isTemplateVisible}>
              <InputTemplateBadge
                onClick={onTemplateClick}
                isSelected={isTemplateVisible ?? false}
              />
            </div>
          )}
          {!!helperText && (
            <div data-error={error} className={styles.helperText}>
              {helperText}
            </div>
          )}
        </div>
      </>
    );
  },
);
